import { GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { auth, db } from '../config';
import { handleAuthError } from './handlers/authError';
import { toast } from 'sonner';
import type { AuthResult, UserData } from './types';
import { nameTranslationService } from '@/lib/services/nameTranslationService';

const provider = new GoogleAuthProvider();
provider.setCustomParameters({
  prompt: 'select_account'
});

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

let nameInputDialogCallback: ((userData: UserData) => void) | null = null;

export function setOnNameInputComplete(callback: (userData: UserData) => void) {
  nameInputDialogCallback = callback;
}

async function waitForUserData(userId: string, retries = 0): Promise<UserData | null> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return {
        ...data,
        id: userId,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt : Timestamp.now(),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt : Timestamp.now(),
        roles: Array.isArray(data.roles) ? data.roles.map(role => ({
          ...role,
          assignedAt: role.assignedAt instanceof Timestamp ? role.assignedAt : Timestamp.now()
        })) : [{
          role: 'customer',
          assignedAt: Timestamp.now(),
          assignedBy: 'system',
          isActive: true
        }]
      } as UserData;
    }
    
    if (retries < MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return waitForUserData(userId, retries + 1);
    }
    
    return null;
  } catch (error) {
    if (retries < MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return waitForUserData(userId, retries + 1);
    }
    throw error;
  }
}

export async function signInWithGoogle(): Promise<AuthResult> {
  try {
    // Configure Google provider
    provider.addScope('email');
    provider.addScope('profile');
    
    // Try sign in with popup
    const { user } = await signInWithPopup(auth, provider);
    
    if (!user.email) {
      throw new Error('Email is required for sign in');
    }

    console.log('Google user data:', {
      photoURL: user.photoURL,
      displayName: user.displayName,
      email: user.email
    });
    
    // Check if user document exists
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (!userDoc.exists()) {
      // Check global signup setting before creating new user
      const globalSignupDoc = await getDoc(doc(db, 'settings', 'globalSignup'));
      const signupEnabled = globalSignupDoc.exists() ? globalSignupDoc.data().enabled !== false : true;
      
      if (!signupEnabled) {
        // Sign out the user since we can't create an account
        await auth.signOut();
        throw new Error('auth.googleSignupDisabled');
      }
      try {
        // Split display name into first and last name
        const nameParts = user.displayName?.split(' ') || ['', ''];
        const firstName = nameParts[0] || 'User';
        const lastName = nameParts.slice(1).join(' ') || 'User';

        // Use AI to translate the name to the other language
        let firstNameEn = firstName;
        let lastNameEn = lastName;
        let firstNameAr = firstName;
        let lastNameAr = lastName;

        try {
          // Auto-translate names using AI - this will detect the language and translate accordingly
          const translatedNames = await nameTranslationService.autoTranslate(firstName, lastName);
          firstNameEn = translatedNames.firstNameEn;
          lastNameEn = translatedNames.lastNameEn;
          firstNameAr = translatedNames.firstNameAr;
          lastNameAr = translatedNames.lastNameAr;
          console.log('AI translated names:', translatedNames);
        } catch (translationError) {
          console.warn('Name translation failed, using original names:', translationError);
          // Fallback to original names if translation fails
        }

        // Create new user document with AI-translated names
        const userData: UserData = {
          id: user.uid,
          email: user.email,
          firstNameEn,
          lastNameEn,
          firstNameAr,
          lastNameAr,
          phone: user.phoneNumber || '',
          photoURL: user.photoURL || '',
          roles: [{
            role: 'customer',
            assignedAt: new Date(),
            assignedBy: 'system',
            isActive: true
          }],
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };

        console.log('Creating new user with AI-translated data:', userData);

        await setDoc(doc(db, 'users', user.uid), userData);
        
        // Wait for the user data to be available
        const createdUserData = await waitForUserData(user.uid);
        if (!createdUserData) {
          throw new Error('Failed to create user account. Please try again.');
        }
        
        // If we have a callback for name input, call it
        if (nameInputDialogCallback) {
          nameInputDialogCallback(createdUserData);
        }
        
        return { user, userData: createdUserData };
      } catch (error) {
        console.error('Error creating new user:', error);
        // If we fail to create the user document, sign out the user
        await auth.signOut();
        throw new Error('Failed to create user account. Please try again.');
      }
    }

    // Return existing user data
    const data = userDoc.data();
    const userData: UserData = {
      ...data,
      id: user.uid,
      email: user.email,
      firstNameEn: data.firstNameEn || '',
      lastNameEn: data.lastNameEn || '',
      firstNameAr: data.firstNameAr || '',
      lastNameAr: data.lastNameAr || '',
      phone: data.phone || '',
      photoURL: user.photoURL || '',
      roles: Array.isArray(data.roles) ? data.roles.map(role => ({
        ...role,
        assignedAt: role.assignedAt instanceof Timestamp ? role.assignedAt : Timestamp.now()
      })) : [{
        role: 'customer',
        assignedAt: Timestamp.now(),
        assignedBy: 'system',
        isActive: true
      }],
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt : Timestamp.now(),
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt : Timestamp.now()
    };

    return { user, userData };
  } catch (error: any) {
    // Handle specific auth errors
    if (error.code === 'auth/popup-blocked') {
      toast.error('Please allow popups for this site to sign in with Google');
      throw new Error('Popup was blocked. Please allow popups and try again.');
    }
    
    if (error.code === 'auth/unauthorized-domain') {
      toast.error('This domain is not authorized for Google sign-in. Please contact support.');
      throw new Error('Unauthorized domain for Google sign-in');
    }

    if (error.code === 'auth/cancelled-popup-request') {
      throw new Error('Sign in was cancelled');
    }

    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign in was cancelled');
    }

    console.error('Google sign in error:', error);
    throw handleAuthError(error);
  }
}